import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LandingPage from "./pages/LandingPage";
import TryonPage from "./pages/TryonPage";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AlbumsPage from "./pages/AlbumsPage";
import SeasonalGalleryPage from "./pages/SeasonalGalleryPage";
import UserDashboard from "./pages/UserDashboard";
import ProfilePage from "./pages/ProfilePage";
import ProfileSetupPage from "./pages/ProfileSetupPage";
import SettingsPage from "./pages/SettingsPage";
import RecentSharesPage from "./pages/RecentSharesPage";
import MerchantDashboard from "./pages/MerchantDashboard";
import BrandManagement from "./pages/BrandManagement";
import ProductManagement from "./pages/ProductManagement";
import Marketplace from "./pages/Marketplace";
import BrandStore from "./pages/BrandStore";
import TeamManagement from "./pages/TeamManagement";
import InStoreModeEnhanced from "./pages/InStoreModeEnhanced";
import MyFavorites from "./pages/MyFavorites";
import MyConsumers from "./pages/MyConsumers";
import AccessoriesTryOn from "./pages/AccessoriesTryOn";
import CreateCampaign from "./pages/CreateCampaign";
import CampaignAnalytics from "./pages/CampaignAnalytics";
import BoutiquePage from "./pages/BoutiquePage";
import InstallPrompt from "./components/InstallPrompt";

// Helper function to check if profile is complete
const isProfileComplete = (user) => {
  return user && user.gender && user.age;
};

// Protected Route Component
const ProtectedRoute = ({ children, requireSetup = true }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #F8F5FF 0%, #FAF8F5 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: { pathname: '/tryon' } }} replace />;
  }

  // Check if profile setup is required (except for setup and profile pages)
  if (requireSetup && !isProfileComplete(user)) {
    return <Navigate to="/setup" replace />;
  }

  return children;
};

function App() {
  return (
    <div className="App">
      <InstallPrompt />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route
              path="/setup"
              element={
                <ProtectedRoute requireSetup={false}>
                  <ProfileSetupPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tryon"
              element={
                <ProtectedRoute>
                  <TryonPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accessories"
              element={
                <ProtectedRoute>
                  <AccessoriesTryOn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/albums"
              element={
                <ProtectedRoute>
                  <AlbumsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seasonal-gallery"
              element={
                <ProtectedRoute>
                  <SeasonalGalleryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute requireSetup={false}>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shares"
              element={
                <ProtectedRoute>
                  <RecentSharesPage />
                </ProtectedRoute>
              }
            />
            <Route path="/models" element={<ProtectedRoute><AlbumsPage /></ProtectedRoute>} />
            <Route path="/collections" element={<ProtectedRoute><SeasonalGalleryPage /></ProtectedRoute>} />
            
            {/* Merchant Routes */}
            <Route path="/merchant" element={<ProtectedRoute><MerchantDashboard /></ProtectedRoute>} />
            <Route path="/merchant/brands" element={<ProtectedRoute><BrandManagement /></ProtectedRoute>} />
            <Route path="/merchant/brands/:brandId" element={<ProtectedRoute><BrandManagement /></ProtectedRoute>} />
            <Route path="/merchant/brands/:brandId/team" element={<ProtectedRoute><TeamManagement /></ProtectedRoute>} />
            <Route path="/merchant/products" element={<ProtectedRoute><ProductManagement /></ProtectedRoute>} />
            <Route path="/merchant/instore" element={<ProtectedRoute><InStoreModeEnhanced /></ProtectedRoute>} />
            <Route path="/merchant/consumers" element={<ProtectedRoute><MyConsumers /></ProtectedRoute>} />
            <Route path="/merchant/campaigns/create" element={<ProtectedRoute><CreateCampaign /></ProtectedRoute>} />
            <Route path="/merchant/campaigns" element={<ProtectedRoute><CampaignAnalytics /></ProtectedRoute>} />
            
            {/* Marketplace Routes */}
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/marketplace/brands/:brandId" element={<BrandStore />} />
            <Route path="/favorites" element={<ProtectedRoute><MyFavorites /></ProtectedRoute>} />
            <Route path="/boutique" element={<ProtectedRoute><BoutiquePage /></ProtectedRoute>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;